import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Shield, Heart, Clock, Users, Briefcase, Baby, UserCheck, Phone } from "lucide-react";
import LeadForm from "../components/LeadForm";
import { SectionEyebrow, FadeInSection, PartnerStrip, TestimonialsSection } from "../components/SharedSections";

const LifeInsurancePage = () => {
  useEffect(() => {
    document.title = "Life Insurance BC | Independent Advisor | Mayker Financial";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", "Get a free life insurance quote from Mayker Financial — BC's independent life insurance advisors. We compare top Canadian insurers to find the right coverage for your family.");
    }
    window.scrollTo(0, 0);
  }, []);

  const coverageTypes = [
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Term Life Insurance",
      description: "Affordable coverage for a set period — ideal for young families, mortgages, and income replacement. Get protected for the years that matter most."
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Whole Life Insurance",
      description: "Permanent coverage that builds cash value over time. A long-term asset that protects your family and grows your wealth."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Permanent Life Insurance",
      description: "Lifelong protection with flexible premium options. Lock in your insurability today and leave a lasting legacy for the people you love."
    }
  ];

  const whoWeServe = [
    {
      icon: <Baby className="w-5 h-5" />,
      title: "Young families",
      description: "Protecting your mortgage, income, and children's future"
    },
    {
      icon: <Briefcase className="w-5 h-5" />,
      title: "Business owners",
      description: "Key person insurance, buy-sell agreements, corporate life insurance"
    },
    {
      icon: <Users className="w-5 h-5" />,
      title: "Parents",
      description: "Making sure your family is taken care of no matter what"
    },
    {
      icon: <UserCheck className="w-5 h-5" />,
      title: "Seniors",
      description: "Final expense coverage and legacy planning"
    }
  ];

  const steps = [
    {
      num: "1",
      title: "Free consultation",
      description: "We start with a 15-minute call to understand your situation, goals, and budget. No paperwork, no pressure."
    },
    {
      num: "2",
      title: "We do the shopping",
      description: "We compare policies across Canada's top insurers and come back with clear, honest recommendations tailored to you."
    },
    {
      num: "3",
      title: "You get covered",
      description: "We handle all the paperwork and walk you through every step. You're protected and confident in your coverage."
    }
  ];

  return (
    <div data-testid="life-insurance-page">
      {/* Hero */}
      <section className="bg-[#0C1B33] pt-28 lg:pt-32 pb-16 lg:pb-20 px-4 lg:px-12 relative">
        <div className="hero-bottom-line" />
        <div className="max-w-6xl mx-auto">
          <FadeInSection>
            <SectionEyebrow light>Life Insurance · British Columbia</SectionEyebrow>
            <h1 className="font-playfair text-3xl lg:text-5xl font-bold text-white leading-tight mb-5 max-w-3xl">
              The right life insurance for your family — <em className="italic text-[#C9A84C]">at the right price.</em>
            </h1>
            <p className="text-base lg:text-lg text-white/60 leading-relaxed max-w-2xl mb-8">
              As independent advisors, we compare policies from Canada's top insurers to find coverage that fits your life, your budget, and your goals. No pressure. Just honest advice.
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="#quote-form"
                className="inline-block bg-[#C9A84C] text-[#0C1B33] text-sm font-medium tracking-wide py-3.5 px-8 rounded hover:bg-[#E8CC80] transition-colors"
              >
                Get a Free Quote →
              </a>
              <a 
                href="tel:7788361962"
                className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-medium py-3.5 px-4 transition-colors"
              >
                <Phone className="w-4 h-4" />
                Call 778-836-1962
              </a>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* Coverage Types */}
      <section className="bg-[#FAF8F3] py-16 lg:py-20 px-4 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <FadeInSection>
            <SectionEyebrow>What we offer</SectionEyebrow>
            <h2 className="font-playfair text-2xl lg:text-4xl font-semibold text-[#0C1B33] leading-tight mb-10 lg:mb-12">
              Coverage for every stage of life
            </h2>
          </FadeInSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {coverageTypes.map((type, index) => (
              <FadeInSection key={index}>
                <div className="bg-white border border-black/5 rounded-lg p-6 h-full pillar-card">
                  <div className="w-12 h-12 bg-[#C9A84C]/10 rounded-lg flex items-center justify-center mb-4 text-[#C9A84C]">
                    {type.icon}
                  </div>
                  <h3 className="font-playfair text-lg font-semibold text-[#0C1B33] mb-2">
                    {type.title}
                  </h3>
                  <p className="text-sm text-[#6B7A99] leading-relaxed">
                    {type.description}
                  </p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* Why Independent */}
      <section className="bg-[#0C1B33] py-16 lg:py-20 px-4 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <FadeInSection>
              <SectionEyebrow light>Why it matters</SectionEyebrow>
              <h2 className="font-playfair text-2xl lg:text-4xl font-semibold text-white leading-tight mb-5">
                We work for you — <em className="italic text-[#C9A84C]">not an insurance company.</em>
              </h2>
              <p className="text-base text-white/60 leading-relaxed mb-6">
                Most advisors are tied to one insurer and can only offer you their products. As independent advisors, we have access to policies from Canada's top-rated life insurance companies — including iA Financial and Canada Life.
              </p>
              <p className="text-base text-white/60 leading-relaxed">
                That means we can objectively compare coverage options, pricing, and features to find the best fit for your unique situation. Our loyalty is to you, not a product quota.
              </p>
            </FadeInSection>

            <FadeInSection>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white/5 border border-white/10 rounded-lg p-5 text-center">
                  <div className="font-playfair text-3xl font-bold text-[#C9A84C] mb-1">10+</div>
                  <div className="text-xs text-white/40">Insurers Compared</div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-lg p-5 text-center">
                  <div className="font-playfair text-3xl font-bold text-[#C9A84C] mb-1">500+</div>
                  <div className="text-xs text-white/40">Families Protected</div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-lg p-5 text-center">
                  <div className="font-playfair text-3xl font-bold text-[#C9A84C] mb-1">15+</div>
                  <div className="text-xs text-white/40">Years Experience</div>
                </div>
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      <PartnerStrip />

      {/* How It Works */}
      <section className="bg-[#FAF8F3] py-16 lg:py-20 px-4 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <FadeInSection>
            <SectionEyebrow>Our process</SectionEyebrow>
            <h2 className="font-playfair text-2xl lg:text-4xl font-semibold text-[#0C1B33] leading-tight mb-10 lg:mb-12">
              How we find your perfect policy
            </h2>
          </FadeInSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {steps.map((step, index) => (
              <FadeInSection key={index}>
                <div className="text-center">
                  <div className="step-number bg-[#0C1B33] text-[#C9A84C] mx-auto mb-4">
                    {step.num}
                  </div>
                  <h3 className="font-playfair text-lg font-semibold text-[#0C1B33] mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-[#6B7A99] leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="bg-[#F0EDE4] py-16 lg:py-20 px-4 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <FadeInSection>
            <SectionEyebrow>Who we help</SectionEyebrow>
            <h2 className="font-playfair text-2xl lg:text-4xl font-semibold text-[#0C1B33] leading-tight mb-10 lg:mb-12">
              Life insurance for every situation
            </h2>
          </FadeInSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {whoWeServe.map((item, index) => (
              <FadeInSection key={index}>
                <div className="bg-white border border-black/5 rounded-lg p-5 h-full">
                  <div className="w-10 h-10 bg-[#C9A84C]/10 rounded-lg flex items-center justify-center mb-3 text-[#C9A84C]">
                    {item.icon}
                  </div>
                  <h3 className="font-medium text-[#0C1B33] mb-1">{item.title}</h3>
                  <p className="text-sm text-[#6B7A99]">{item.description}</p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Form */}
      <section id="quote-form" className="bg-[#0C1B33] py-16 lg:py-20 px-4 lg:px-12">
        <div className="max-w-xl mx-auto">
          <FadeInSection>
            <div className="bg-[#FAF8F3] rounded-xl p-6 lg:p-8 card-top-accent">
              <LeadForm 
                phoneRequired
                intentRequired
                title="Get your free life insurance quote"
                subtitle="Takes 60 seconds. We respond within 1 business day."
                intentOptions={[
                  { value: "Term life insurance", label: "Term life insurance" },
                  { value: "Whole life insurance", label: "Whole life insurance" },
                  { value: "Permanent life insurance", label: "Permanent life insurance" },
                  { value: "Not sure — need guidance", label: "Not sure — need guidance" },
                ]}
              />
            </div>
          </FadeInSection>
        </div>
      </section>

      <TestimonialsSection />
    </div>
  );
};

export default LifeInsurancePage;
